/**
 * Income-tax (ITR) Assessment domain types.
 *
 * Aligned to the Tax Assessment Service contract
 * (taxhive-tax-assessment-service `TaxAssessmentResponse`), mapped to the UI's
 * camelCase convention. This is the canonical assessment type for the
 * assessments workflow (service + tab + detail page).
 *
 * Backward-compat: the field names here are a SUPERSET of the inline
 * `Assessment` type historically declared in `@/types/customer` (same
 * `financialYear`, `assessmentYear`, `itrType`, `status`, `dueDate`,
 * `filedDate`, `acknowledgeNumber`, `taxableIncome`, `refundAmount`,
 * `demandAmount`, `customerId`, `id`), so `customer.assessments` data is
 * structurally assignable to it. `types/customer.ts` is intentionally left
 * untouched to avoid churn in widely-imported call sites.
 */

// ============================================================================
// ENUMS / UNIONS (mirror the backend enums)
// ============================================================================

export type AssessmentStatus =
  | 'not_started'
  | 'documents_pending'
  | 'in_progress'
  | 'under_review'
  | 'filed'
  | 'processed'
  | 'refund_issued'
  | 'demand_raised'

export type ITRType =
  | 'ITR-1'
  | 'ITR-2'
  | 'ITR-3'
  | 'ITR-4'
  | 'ITR-5'
  | 'ITR-6'
  | 'ITR-7'

export type TaxRegime = 'old' | 'new'

export type EVerificationStatus = 'pending' | 'verified' | 'failed'

// ============================================================================
// ASSESSMENT
// ============================================================================

export interface Assessment {
  id: string
  /** Exact source revision for authorized retained histories; absent on legacy records. */
  sourceVersionId?: string
  customerId: string
  /** e.g. "2024-25" */
  financialYear: string
  /** e.g. "2025-26" */
  assessmentYear: string
  itrType: ITRType
  taxRegime?: TaxRegime
  status: AssessmentStatus
  pan?: string

  // Dates (ISO strings)
  dueDate: string
  filedDate?: string
  processedDate?: string
  acknowledgementDate?: string

  // Identifiers
  acknowledgeNumber?: string
  eVerificationStatus?: EVerificationStatus

  // Income & tax figures
  grossIncome?: number
  grossTotalIncome?: number
  totalDeductions?: number
  taxableIncome?: number
  taxOnIncome?: number
  totalTaxLiability?: number
  taxPaid?: number
  refundAmount?: number
  demandAmount?: number

  notes?: string
  createdDate?: string
  lastUpdated?: string
}

// ============================================================================
// INPUT TYPES
// ============================================================================

export interface CreateAssessmentInput {
  customerId: string
  pan: string
  financialYear: string
  assessmentYear: string
  itrType: ITRType
  taxRegime?: TaxRegime
  /** Defaults to "individual" on the backend. */
  customerType?: string
  dueDate?: string

  // Optional income seed fields
  salaryIncome?: number
  housePropertyIncome?: number
  businessIncome?: number
  otherIncome?: number
  totalDeductions?: number
}

export interface UpdateAssessmentInput {
  status?: AssessmentStatus
  filedDate?: string
  processedDate?: string
  acknowledgeNumber?: string
  acknowledgementDate?: string
  eVerificationStatus?: EVerificationStatus
  taxRegime?: TaxRegime

  salaryIncome?: number
  housePropertyIncome?: number
  businessIncome?: number
  otherIncome?: number
  totalDeductions?: number
  refundAmount?: number
  demandAmount?: number
}

// ============================================================================
// ASSESSMENT DOCUMENTS
// ============================================================================

export interface AssessmentDocument {
  id: string
  assessmentId: string
  assessmentType: string
  documentId: string
  category?: string
  displayName?: string
  isMandatory: boolean
  isReceived: boolean
  receivedDate?: string
  isVerified: boolean
  verifiedBy?: string
  verifiedByName?: string
  verifiedAt?: string
  verificationNotes?: string
  linkedBy?: string
  linkedByName?: string
  linkedAt: string
}

export interface LinkAssessmentDocumentInput {
  documentId: string
  category?: string
  displayName?: string
  isMandatory?: boolean
}

// ============================================================================
// TAX CALCULATION (POST /tax-assessments/calculate-tax)
// ============================================================================

export interface TaxCalculationInput {
  grossIncome: number
  totalDeductions?: number
  assessmentYear: string
  taxRegime?: TaxRegime
}

export interface TaxCalculationResult {
  grossIncome: number
  totalDeductions: number
  taxableIncome: number
  taxOnIncome: number
  surcharge: number
  educationCess: number
  totalTaxLiability: number
  taxRegime: TaxRegime
  assessmentYear: string
  breakdown?: Record<string, unknown>
}

// ============================================================================
// DISPLAY HELPERS
// ============================================================================

export const ASSESSMENT_STATUS_LABEL: Record<AssessmentStatus, string> = {
  not_started: 'Not Started',
  documents_pending: 'Documents Pending',
  in_progress: 'In Progress',
  under_review: 'Under Review',
  filed: 'Filed',
  processed: 'Processed',
  refund_issued: 'Refund Issued',
  demand_raised: 'Demand Raised',
}

/** Ordered status pipeline for the detail-page timeline. */
export const ASSESSMENT_STATUS_FLOW: AssessmentStatus[] = [
  'not_started',
  'documents_pending',
  'in_progress',
  'under_review',
  'filed',
  'processed',
]

/** Statuses that mean the assessment is still actionable ("Continue"). */
export const OPEN_ASSESSMENT_STATUSES: AssessmentStatus[] = [
  'not_started',
  'documents_pending',
  'in_progress',
]

export const ITR_TYPES: ITRType[] = [
  'ITR-1',
  'ITR-2',
  'ITR-3',
  'ITR-4',
  'ITR-5',
  'ITR-6',
  'ITR-7',
]
